<script setup lang="ts">
import { useOrderStore } from '../stores/order'
import type { EntranceSchemaData } from '@/types'
import type { IEvent } from '@/types'
import { ORDER_LIMIT } from '../utils/constants'

const props = defineProps<{
  event: IEvent<EntranceSchemaData>
  loading: boolean
  createOrder: Function
  commission: number
  settings: {
    percentage: {
      offline: { value: number }
      online: { value: number }
    }
    features: {
      offlineSales: boolean
      onlineSales: boolean
      externalPercentage: boolean
    }
  }
}>()

const { event } = toRefs(props)

const orderStore = useOrderStore()

const { moneyFormater } = useMoneyFormater()
const { formatDate } = useDate()

const cart = computed<Record<string, EntranceSchemaData>>({
  get: () => {
    return orderStore.cart as Record<string, EntranceSchemaData>
  },
  set: (value: Record<string, EntranceSchemaData>) => {
    orderStore.setCart(value)
  }
})

cart.value = JSON.parse(JSON.stringify(event.value.tariff.schema))

for (let key in cart.value) {
  cart.value[key].count = '0'
}

const isLimitOrder = ref(false)

function addToCart(key: string) {
  if (cartLength.value >= ORDER_LIMIT) {
    isLimitOrder.value = true
    setTimeout(() => {
      isLimitOrder.value = false
    }, 2000)
  } else if (Number(event.value.tariff.schema[key].count) === -1) {
    cart.value[key].count = String(Number(cart.value[key].count) + 1)
  } else if (Number(cart.value[key].count) < Number(event.value.tariff.schema[key].count))
    cart.value[key].count = String(Number(cart.value[key].count) + 1)
  else {
    alert('Больше нет доступных мест')
  }
}
function removeFromCart(key: string) {
  if (Number(cart.value[key].count) > 0)
    cart.value[key].count = String(Number(cart.value[key].count) - 1)
}

const cartLength = computed<number>(() => {
  return Object.values(cart.value).reduce((prev, next) => {
    prev += Number(next.count)
    return prev
  }, 0)
})

const cartAmount = computed<number>(() => {
  return Object.values(cart.value).reduce((prev, next) => {
    prev += Number(next.price) * Number(next.count)
    return prev
  }, 0)
})

const commissionAmount = computed<number>(() => {
  return Object.values(cart.value).reduce((prev, next) => {
    prev += Number(next.price) * Number(next.count) * (props.commission / 100)
    return prev
  }, 0)
})

function countTickets(count:number){
  if(count===-1) { return ''}
  else if(count===0) {
    return 'билетов нет'
  }
  else if(count===1) {
    return `1 билет`
  }
  else if(count>1&& count<=4) {
    return `${count} билета`
  }
  else if(count>4) {
    return `${count} билетов`
  }
}
</script>

<template>
  <section class="relative mx-auto h-full w-full flex flex-col justify-between relative">
    <div class="py-5 pl-6 pr-3" style="height: 73%">
      <div class="flex mb-4 flex-row justify-between pr-3">
        <div class="font-sf-pro text-gray-300 lg:text-lg text-base">
          {{ formatDate(event.scheduleInfo.sessionDate, 'D MMMM HH:mm') }}
        </div>
        <div
          class="hidden lg:flex flex-row space-x-2 text-base font-sf-pro text-gray-300 font-semibold"
        >
          <span class="font-normal"> {{ event.organization.name }} </span>
        </div>
      </div>
      <div class="mb-5 font-sf-pro text-gray-900 font-semibold text-xl lg:text-5xl">
        {{ event.activity.title }}
      </div>
      <main class="overflow-y-auto h-full pr-3" id="scrollBlock">
        <template v-for="(item, index) in event.tariff.schema">
          <div
            class="flex flex-row lg:flex-row justify-between py-7 border-b-2 border-gray-400 items-center"
          >
            <div>
              <div
                class="text-base lg:text-xl font-sf-pro text-gray-900 font-normal lg:font-medium"
              >
                {{ item.title }}
              </div>

              <div
                class="flex flex-row items-end space-x-3 lg:hidden "
              >
                <p class="text-xl font-sf-pro text-gray-900 font-normal lg:font-semibold">{{ moneyFormater(Number(item.price)) }}</p>
                <!-- <p class="text-gray-300 whitespace-nowrap text-sm"> 
                {{countTickets(item.count)}}
                </p> -->
              </div>
            </div>

            <div class="relative flex flex-row lg:space-x-7 items-center">
              <div
                class="hidden lg:flex items-end flex-col space-y-2"
              >
                <p class="m-0 text-xl font-sf-pro text-gray-900  font-normal lg:font-semibold"> {{ moneyFormater(Number(item.price)) }}</p> 
                <!-- <p class="text-gray-300 whitespace-nowrap text-sm" v-if="item.count>=0"> 
                  {{countTickets(item.count)}}
                </p> -->
              </div>
              <div class="flex flex-row space-x-5 items-center">
                <t-minus-primary :isActive="true" @click="removeFromCart(item.id)" />
                <span class="text-xl font-sf-pro w-3 text-gray-900 font-semibold">
                  {{ cart[item.id].count }}
                </span>

                <t-plus-primary :isActive="true" @click="addToCart(item.id)" />
               
              </div>
    
            </div>
          </div>
          <!-- <div class="w-full h-[1px] bg-gray-400 rounded mt-6"></div> -->
        </template>
      </main>
    </div>

    <div
      v-if="isLimitOrder"
      class="absolute left-2/4 right-2/4 top-2/4 xl:top-auto xl:bottom-40 xl:left-auto xl:right-10 bg-black text-white -translate-x-2/4 xl:translate-x-0 rounded-xl px-4 py-4 right-0 w-full max-h-21 max-w-90"
    >
      <w-icon-close
        class="absolute w-4 h-4 top-4 right-4 cursor-pointer"
        @click="isLimitOrder = false"
      ></w-icon-close>
      <p style="color: #e9e7e4">За один заказ можно купить не более {{ ORDER_LIMIT }} билетов</p>
    </div>

    <div class="hidden absolute bottom-6 bg-white lg:block z-80 w-full" v-if="cartLength > 0">
      <div class="">
        <div class="w-full h-[1px] bg-gray-400 rounded mt-auto"></div>
        <section class="flex flex-row justify-between p-4">
          <div class="flex flex-row">
            <template v-for="item in cart">
              <template v-for="index in Number(item.count)">
                <div class="flex flex-col border-x border-x-gray-400 pb-4 px-3">
                  <div class="flex flex-row space-x-3">
                    <div class="text-base font-sf-pro text-gray-900 font-normal">
                      {{ item.title }}
                    </div>
                    <div>
                      <t-icon-cancel
                        @click="removeFromCart(item.id)"
                        class="w-5 h-5 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div class="text-xl font-sf-pro text-gray-900 font-semibold">
                    {{ moneyFormater(Number(item.price)) }}
                  </div>
                </div>
              </template>
            </template>
          </div>

          <div class="flex flex-row space-x-10 items-center">
            <div class="pl-4 text-xl font-sf-pro text-gray-900 font-semibold">
              <p class="whitespace-nowrap">
                {{ cartLength }}
                {{ cartLength === 1 ? 'билет' : cartLength < 5 ? 'билета' : 'билетов' }}
              </p>
              <p>
                {{
                  settings.features.externalPercentage
                    ? moneyFormater(cartAmount + commissionAmount)
                    : moneyFormater(Number(cartAmount))
                }}
              </p>
              <p
                class="text-sm text-gray-900 font-sf-pro"
                v-if="commissionAmount > 0 && settings.features.externalPercentage"
              >
                Размер комиссии
                {{ moneyFormater(commissionAmount) }}
              </p>
            </div>
            <t-button rounded="large" class="w-31" @click="createOrder" :loading="loading"
              >Далее</t-button
            >
          </div>
        </section>
      </div>
    </div>

    <div
      class="lg:hidden block absolute bottom-0 w-full bg-white z-80 h-30 p-4 rounded-[12px] flex flex-col space-y-4"
      style="box-shadow: 0px -3px 12px #0000000f"
      v-if="cartLength > 0"
    >
      <div class="flex flex-row space-x-2 items-center">
        <span class="text-sm text-gray-900 font-sf-pro">
          {{ cartLength }}
          {{ cartLength === 1 ? 'билет' : cartLength < 5 ? 'билета' : 'билетов' }}:
        </span>
        <span class="text-primary-100 text-xl font-semibold font-sf-pro">
          {{
            settings.features.externalPercentage
              ? moneyFormater(cartAmount + commissionAmount)
              : moneyFormater(Number(cartAmount))
          }}
        </span>
        <span
          class="text-sm text-gray-900 font-sf-pro"
          v-if="commissionAmount > 0 && settings.features.externalPercentage"
        >
          Размер комиссии
          {{ moneyFormater(commissionAmount) }}:
        </span>
      </div>
      <div class="w-full h-[1px] bg-gray-400 rounded mt-auto"></div>

      <t-button
        rounded="large"
        height="medium"
        class="w-full sm:w-31"
        @click="createOrder"
        :loading="loading"
      >
        Далее
      </t-button>
    </div>
  </section>
</template>

<style scoped>
#scrollBlock {
  overflow-y: scroll;
  max-width: 100%;
}

#scrollBlock::-webkit-scrollbar {
  height: 2px;
  width: 7px !important;
  border-radius: 9px !important;
}

#scrollBlock::-webkit-scrollbar-track {
  background-color: #fefefe;
}

#scrollBlock::-webkit-scrollbar-thumb {
  width: 7px;
  border-radius: 9px;
  background-color: #0575dd;
}
</style>
